// REMOVED: OAuth token imports - now using API key authentication
// import { clearCachedYouTubeToken, getYouTubeAccessToken } from "./youtubeAuth";
import { YOUTUBE_API_KEY } from "../../../config/api.config";
import {
  YouTubeChannelSearchDetails,
  YouTubeSearchResult,
} from "../../../types/outreach";
import {
  YouTubeSearchParams,
  YouTubeSearchOptions,
  YouTubeSearchResponse,
} from "../types";

// Re-export types for external use
export type {
  YouTubeChannelSearchDetails,
  YouTubeSearchResult,
  YouTubeSearchParams,
  YouTubeSearchOptions,
  YouTubeSearchResponse,
};

// API error logging utility
function logApiError(
  operationName: string,
  error: unknown,
  context: {
    url?: string;
    method?: string;
    status?: number;
    query?: string;
    pageToken?: string;
  },
) {
  const timestamp = new Date().toISOString();
  const errorMessage = error instanceof Error ? error.message : String(error);

  const logData = {
    timestamp,
    operationName,
    errorMessage,
    context,
  };

  console.error(
    `[YouTube API Error - ${operationName}]`,
    JSON.stringify(logData, null, 2),
    error,
  );
}

async function fetchWithRetry(
  url: string,
  options: RequestInit = {},
  retries = 3,
): Promise<Response> {
  try {
    const response = await fetch(url, options);

    if (response.status === 429 && retries > 0) {
      const delay = Math.pow(2, 4 - retries) * 1000;
      await new Promise((resolve) => setTimeout(resolve, delay));
      return fetchWithRetry(url, options, retries - 1);
    }

    return response;
  } catch (error) {
    if (retries > 0) {
      const delay = Math.pow(2, 4 - retries) * 1000;
      await new Promise((resolve) => setTimeout(resolve, delay));
      return fetchWithRetry(url, options, retries - 1);
    }

    throw error;
  }
}

export async function searchYouTubeChannels(
  params: YouTubeSearchParams,
): Promise<YouTubeSearchResponse> {
  const { query, maxResults = 20, pageToken, order = "relevance" } = params;

  // Parameter validation
  if (!query || query.trim().length === 0) {
    throw new Error("Search query is required and cannot be empty");
  }

  // Validate API key is configured
  if (!YOUTUBE_API_KEY) {
    throw new Error(
      "YouTube API key is not configured. Please set REACT_APP_YOUTUBE_API_KEY in .env file."
    );
  }

  // Validate maxResults is within YouTube API limits (1-50)
  const validatedMaxResults = Math.min(Math.max(maxResults, 1), 50);
  if (validatedMaxResults !== maxResults) {
    console.warn(
      `maxResults ${maxResults} adjusted to valid range [1, 50]: ${validatedMaxResults}`,
    );
  }

  // Validate order parameter
  // YouTube API supports: relevance, date, rating, title, videoCount, viewCount
  // We expose only: relevance, viewCount, title (subscribers is not a valid YouTube API sort)
  const validOrders = ["relevance", "viewCount", "title"] as const;
  if (!validOrders.includes(order as any)) {
    throw new Error(
      `Invalid order parameter: ${order}. Must be one of: ${validOrders.join(", ")}`,
    );
  }

  const searchUrl = new URL("https://www.googleapis.com/youtube/v3/search");
  // API Key authentication - simpler and sufficient for public channel search
  searchUrl.searchParams.append("key", YOUTUBE_API_KEY);
  searchUrl.searchParams.append("part", "snippet");
  searchUrl.searchParams.append("q", query);
  searchUrl.searchParams.append("type", "channel");
  searchUrl.searchParams.append("order", order);
  searchUrl.searchParams.append("maxResults", validatedMaxResults.toString());
  if (pageToken) searchUrl.searchParams.append("pageToken", pageToken);

  try {
    // Simple fetch - no OAuth headers needed with API key
    const response = await fetchWithRetry(searchUrl.toString());

    // Handle specific error responses with pagination context
    if (!response.ok) {
      if (response.status === 400 && pageToken) {
        throw new Error(`API error: 400 - Invalid page token`);
      } else if (response.status === 403) {
        throw new Error(`API error: 403 - quota exceeded`);
      } else if (response.status === 429) {
        throw new Error(`API error: 429 - rate limited`);
      } else if (response.status === 401) {
        throw new Error(`API error: 401 - unauthorized`);
      }
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();

    const channelIds: string[] = (data.items || [])
      .map((item: any) => item.id?.channelId)
      .filter(
        (id: unknown): id is string => typeof id === "string" && id.length > 0,
      );

    const channelStats = new Map<
      string,
      { subscriberCount?: number; viewCount?: number; videoCount?: number }
    >();

    if (channelIds.length > 0) {
      const channelsUrl = new URL(
        "https://www.googleapis.com/youtube/v3/channels",
      );
      // Use API key for channels endpoint too
      channelsUrl.searchParams.append("key", YOUTUBE_API_KEY);
      channelsUrl.searchParams.append("part", "snippet,statistics");
      channelsUrl.searchParams.append("id", channelIds.join(","));

      const channelsResponse = await fetchWithRetry(channelsUrl.toString());

      if (!channelsResponse.ok) {
        throw new Error(`API error: ${channelsResponse.status}`);
      }

      const channelsData = await channelsResponse.json();
      (channelsData.items || []).forEach((channel: any) => {
        if (!channel?.id) {
          return;
        }

        const subscriberCount = channel.statistics?.subscriberCount
          ? parseInt(channel.statistics.subscriberCount, 10)
          : undefined;
        const viewCount = channel.statistics?.viewCount
          ? parseInt(channel.statistics.viewCount, 10)
          : undefined;
        const videoCount = channel.statistics?.videoCount
          ? parseInt(channel.statistics.videoCount, 10)
          : undefined;

        channelStats.set(channel.id, {
          subscriberCount,
          viewCount,
          videoCount,
        });
      });
    }

    // Map items to YouTubeSearchResult format
    // IMPORTANT: Mapping over data.items preserves the search API's sort order.
    // We look up statistics by channel ID from the Map (which doesn't affect order),
    // so the final result maintains the API-level sort order from the Search endpoint.
    let mappedItems: YouTubeSearchResult[] = [];
    try {
      mappedItems = (data.items || []).map((item: any) => ({
        channelId: item.id.channelId,
        channelTitle: item.snippet.title,
        channelDescription: item.snippet.description,
        thumbnailUrl: item.snippet.thumbnails?.medium?.url || "",
        subscriberCount: channelStats.get(item.id.channelId)?.subscriberCount,
        viewCount: channelStats.get(item.id.channelId)?.viewCount,
        videoCount: channelStats.get(item.id.channelId)?.videoCount,
      }));
    } catch (mappingError) {
      logApiError("searchYouTubeChannels - item mapping", mappingError, {
        url: "https://www.googleapis.com/youtube/v3/search",
        method: "GET",
        query,
        pageToken,
      });
      throw new Error("Failed to map YouTube API response to internal format");
    }

    // Apply Topic channel filter with error handling
    let filteredItems: YouTubeSearchResult[] = mappedItems;
    try {
      filteredItems = filterTopicChannels(mappedItems);
    } catch (filterError) {
      logApiError("searchYouTubeChannels - filtering", filterError, {
        method: "POST_PROCESS",
        query,
      });
      throw new Error("Failed to filter YouTube channels for Topic channels");
    }
    console.info("Data: ", data);
    return {
      items: filteredItems,
      nextPageToken: data.nextPageToken,
      prevPageToken: data.prevPageToken,
      totalResults: filteredItems.length,
      resultsPerPage: validatedMaxResults,
    };
  } catch (error) {
    // Enhanced error handling with specific error messages
    let errorMessage = "Error searching YouTube channels";

    if (error instanceof Error) {
      // Handle specific API errors
      if (error.message.includes("API error: 403")) {
        errorMessage = "YouTube API quota exceeded or access forbidden";
      } else if (error.message.includes("API error: 401")) {
        errorMessage = "YouTube authentication failed or token expired";
      } else if (error.message.includes("API error: 429")) {
        errorMessage = "YouTube API rate limited - please try again later";
      } else if (error.message.includes("API error:")) {
        errorMessage = `YouTube API error: ${error.message}`;
      } else {
        errorMessage = error.message;
      }
    } else if (typeof error === "string") {
      errorMessage = error;
    }

    // Log with context for debugging
    logApiError("searchYouTubeChannels", error, {
      url: "https://www.googleapis.com/youtube/v3/search",
      method: "GET",
      query,
      pageToken,
    });

    throw new Error(errorMessage);
  }
}

export function extractEmailFromDescription(
  description: string,
): string | undefined {
  const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/gi;
  const emails = description.match(emailRegex);
  return emails && emails.length > 0 ? emails[0] : undefined;
}

export async function getChannelDetailsById(
  channelId: string,
): Promise<YouTubeChannelSearchDetails | null> {
  // Validate API key is configured
  if (!YOUTUBE_API_KEY) {
    throw new Error(
      "YouTube API key is not configured. Please set REACT_APP_YOUTUBE_API_KEY in .env file."
    );
  }

  const url = new URL("https://www.googleapis.com/youtube/v3/channels");
  // Use API key authentication
  url.searchParams.append("key", YOUTUBE_API_KEY);
  url.searchParams.append("part", "snippet,contentDetails,statistics");
  url.searchParams.append("id", channelId);

  try {
    const response = await fetchWithRetry(url.toString());

    // Handle specific error responses with proper error messages
    if (!response.ok) {
      if (response.status === 403) {
        throw new Error(`API error: 403 - quota exceeded`);
      } else if (response.status === 429) {
        throw new Error(`API error: 429 - rate limited`);
      } else if (response.status === 401) {
        throw new Error(`API error: 401 - unauthorized`);
      }
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    if (!data.items || data.items.length === 0) return null;

    const channel = data.items[0];
    const email = extractEmailFromDescription(
      channel.snippet.description ?? "",
    );

    return {
      channelId: channel.id,
      title: channel.snippet.title,
      description: channel.snippet.description,
      thumbnails: channel.snippet.thumbnails,
      subscriberCount: channel.statistics.subscriberCount,
      viewCount: parseInt(channel.statistics.viewCount, 10),
      videoCount: parseInt(channel.statistics.videoCount, 10),
      email,
      customUrl: channel.snippet.customUrl,
      country: channel.snippet.country,
      publishedAt: channel.snippet.publishedAt,
    };
  } catch (error) {
    logApiError("getChannelDetailsById", error, {
      url: "https://www.googleapis.com/youtube/v3/channels",
      method: "GET",
      query: channelId,
    });
    throw error;
  }
}

export function filterTopicChannels(
  channels: YouTubeSearchResult[],
): YouTubeSearchResult[] {
  if (!Array.isArray(channels)) {
    console.warn("Invalid channels data provided to filterTopicChannels");
    return [];
  }

  return channels.filter((channel: YouTubeSearchResult): boolean => {
    try {
      const title = (channel.channelTitle || "").toLowerCase();
      const description = (channel.channelDescription || "").toLowerCase();

      return !title.includes("topic") && !description.includes("topic");
    } catch (error) {
      logApiError("filterTopicChannels - channel filter", error, {
        method: "POST_PROCESS",
      });
      // Keep channel on error as a fallback (don't filter out due to processing error)
      return true;
    }
  });
}
